import type { ReactNode } from 'react'
import { useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Menu, toastManager } from '@systemeio/components'
import {
  DotsVerticalIcon,
  Pencil01Icon,
  Pin02Icon,
  ReportMessageIcon,
  Trash02Icon,
  XCloseIcon,
} from '@systemeio/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import Confirmation from 'shared/components/confirmation-modal'
import { featureFlagsClient } from 'shared/feature-flags/client'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import type { ApiPaginationResponseInterface } from 'shared/types/api-pagination-response-interface'
import { useDeleteComment } from 'modules/comments/api/delete-comment'
import { usePinComment } from 'modules/comments/api/pin-comment'
import { useUnpinComment } from 'modules/comments/api/unpin-comment'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import {
  deleteCommentInLinkedList,
  findOptimalCommentPosition,
  insertCommentToLinkedList,
} from 'modules/comments/utils/comments-linked-list'
import { communityKeys, communityReportKeys } from 'modules/community/api/key-factory'
import { banUser } from 'modules/community/api/posts-api'
import { useReportComment } from 'modules/community/api/report-comment'
import { ReportContentModal } from 'modules/community/components/report-content/report-content-modal'
import { ACTIVITY_CLEANUP_PERIOD_DURATION } from 'modules/community/const/post-consts'
import { BanPeriodUnitEnum } from 'modules/community/enums/member-enum'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { RemoveRecentActivitiesCheckbox } from 'modules/community/post/remove-recent-activities-checkbox'
import type { PostCommentsInfoInterface } from 'modules/community/types/post-info'

interface CommentActionsProps {
  onEdit: () => void
  className?: string
  commentData: CommentInterface
}

export default function CommentActions({
  onEdit,
  className,
  commentData,
}: CommentActionsProps): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const {
    extra,
    commentsList,
    setComments,
    updateReplies,
    repliesList,
    pinnedComments,
    setPinnedComments,
    currentSortOption,
  } = useCommentsContext()
  const pinCommentMutation = usePinComment({ commentId: commentData.id })
  const unpinCommentMutation = useUnpinComment({ commentId: commentData.id })
  const reportCommentMutation = useReportComment({})

  const [removeRecentActivitiesChecked, setRemoveRecentActivitiesChecked] = useState(false)
  const [isDeleteAndBanUserOpened, setIsDeleteAndBanUserOpened] = useState(false)
  const [isDeleteCommentOpened, setIsDeleteCommentOpened] = useState(false)
  const [switchPinnedCommentModalOpened, setSwitchPinnedCommentModalOpened] = useState(false)
  const [isReportOpened, setIsReportOpened] = useState(false)

  const { canDeleteComment, canDeleteCommentAndBanAuthor, canUpdateComment, canPinComment } =
    commentData
  const isCommunityComments = isCommunityExtra(extra)
  const communityPath = isCommunityComments ? extra.community.communityPath : ''
  const postId = isCommunityComments ? extra.community.postId : 0
  const { isUserMember } = useIsUserMember(communityPath || undefined)

  const isOwnComment = Boolean(user && isAuthorizedUser(user) && user.id === commentData.userId)

  const isReportsEnabled = featureFlagsClient.isOn(featureFlagKeys.enableCommunityReports)
  const canReportComment =
    isReportsEnabled &&
    Boolean(isUserMember) &&
    Boolean(communityPath) &&
    postId > 0 &&
    !isOwnComment

  const isCanManagePinsAndNotReplyComment = canPinComment && Array.isArray(commentData.parent)

  const deleteCommentMutation = useDeleteComment({
    commentId: commentData.id,
    onSuccess: () => {
      if (commentData.parent && !Array.isArray(commentData.parent)) {
        const parentID = commentData.parent.id
        updateReplies({
          commentId: parentID,
          replies: repliesList[parentID].replies?.filter(el => el.id !== commentData.id),
        })

        if (commentData.isPinned) {
          setPinnedComments(
            pinnedComments.map(comment =>
              comment.id === parentID
                ? { ...comment, totalReplies: comment.totalReplies - 1 }
                : comment,
            ),
          )
        } else {
          setComments(
            commentsList.map(comment =>
              comment.id === parentID
                ? { ...comment, totalReplies: comment.totalReplies - 1 }
                : comment,
            ),
          )
        }
      } else {
        if (commentData.isPinned) {
          setPinnedComments(deleteCommentInLinkedList(pinnedComments, commentData.id))
        } else {
          setComments(deleteCommentInLinkedList(commentsList, commentData.id))
        }
      }

      if (isCommunityComments) {
        const deletedCommentsCount = Array.isArray(commentData.parent)
          ? commentData.totalReplies + 1
          : 1
        void queryClient.invalidateQueries({
          queryKey: communityKeys.postsCommentsCount(extra.community.communityPath, [
            extra.community.postId,
          ]),
        })
        queryClient.setQueriesData<ApiPaginationResponseInterface<PostCommentsInfoInterface>>(
          { queryKey: communityKeys.postsCommentsGeneral() },
          oldData => {
            const info = oldData?.items.find(item => item.postId === extra.community.postId)

            if (!oldData || !info) return oldData

            return {
              ...oldData,
              items: oldData.items.map(item =>
                item.postId === extra.community.postId
                  ? {
                      ...item,
                      numberOfComments: Math.max(0, item.numberOfComments - deletedCommentsCount),
                    }
                  : item,
              ),
            }
          },
        )
        void queryClient.invalidateQueries({ queryKey: communityKeys.postsCommentsGeneral() })
        void queryClient.invalidateQueries({ queryKey: communityReportKeys.all })
      }
    },
    onError: error => {
      toastManager.add({ type: 'error', title: t('comments.actions.delete_comment.failed') })
      Sentry.captureException(error)
    },
  })

  const handleDeleteAndBanUser = async (): Promise<void> => {
    if (!isCommunityComments) return
    await deleteCommentMutation.mutateAsync()

    try {
      await banUser(
        commentData.userId,
        extra.community.communityPath,
        removeRecentActivitiesChecked
          ? {
              deleteRecentActivityOptions: {
                periodUnit: BanPeriodUnitEnum.day,
                periodDuration: ACTIVITY_CLEANUP_PERIOD_DURATION,
              },
            }
          : undefined,
      )
      toastManager.add({
        type: 'success',
        title: t('comments.actions.delete_comment_and_ban_user.success'),
      })
    } catch (error) {
      toastManager.add({ type: 'error', title: t('members.ban_failed_toast') })
      throw error
    }
  }

  // The maximum limit of pinned messages is 1. That's why they switch each other
  const handleSwitchPinnedComment = async (): Promise<void> => {
    await pinCommentMutation
      .trigger()
      .then(() => {
        setComments(comments => {
          const fresh = deleteCommentInLinkedList(
            deleteCommentInLinkedList(comments, commentData.id),
            pinnedComments[0].id,
          )
          return insertCommentToLinkedList(
            fresh,
            { ...pinnedComments[0], isPinned: false },
            findOptimalCommentPosition(fresh, pinnedComments[0], currentSortOption),
          )
        })
        setPinnedComments(
          insertCommentToLinkedList(
            deleteCommentInLinkedList(pinnedComments, pinnedComments[0]?.id),
            { ...commentData, isPinned: true },
            null,
          ),
        )
        setSwitchPinnedCommentModalOpened(false)
        toastManager.add({ type: 'success', title: t('comments.actions.pin_comment.success') })
      })
      .catch(() =>
        toastManager.add({ type: 'error', title: t('core.error.internal_error_message') }),
      )
  }

  const handlePinComment = async (): Promise<void> => {
    const isHaveAlreadyPinnedComment = pinnedComments.length > 0
    if (!isHaveAlreadyPinnedComment) {
      await pinCommentMutation
        .trigger()
        .then(() => {
          setComments(comments => deleteCommentInLinkedList(comments, commentData.id))
          setPinnedComments(comments =>
            insertCommentToLinkedList(comments, { ...commentData, isPinned: true }, null),
          )
          toastManager.add({ type: 'success', title: t('comments.actions.pin_comment.success') })
        })
        .catch(() =>
          toastManager.add({ type: 'error', title: t('core.error.internal_error_message') }),
        )
    } else {
      setSwitchPinnedCommentModalOpened(true)
    }
  }

  const handleUnpinComment = async (): Promise<void> => {
    await unpinCommentMutation
      .trigger()
      .then(() => {
        const pinnedComment = { ...pinnedComments[0], isPinned: false }
        setPinnedComments([])
        setComments(comments => {
          const prevId = findOptimalCommentPosition(comments, pinnedComment, currentSortOption)
          return insertCommentToLinkedList(comments, pinnedComment, prevId)
        })
        toastManager.add({ type: 'success', title: t('comments.actions.unpin_comment.success') })
      })
      .catch(() =>
        toastManager.add({ type: 'error', title: t('core.error.internal_error_message') }),
      )
  }

  const menuItems = [
    ...(canDeleteComment
      ? [
          {
            id: 'comment-action-remove',
            label: (
              <>
                <XCloseIcon className="size-5" />
                {t('comments.actions.remove')}
              </>
            ),
            onSelect: () => setIsDeleteCommentOpened(true),
          },
        ]
      : []),
    ...(canUpdateComment
      ? [
          {
            id: 'comment-action-edit',
            label: (
              <>
                <Pencil01Icon className="size-5" />
                {t('comments.actions.edit')}
              </>
            ),
            onSelect: onEdit,
          },
        ]
      : []),
    ...(canDeleteCommentAndBanAuthor && isCommunityComments
      ? [
          {
            id: 'comment-action-remove-and-ban',
            label: (
              <>
                <Trash02Icon className="size-5" />
                {t('comments.actions.remove_and_ban_user')}
              </>
            ),
            onSelect: () => setIsDeleteAndBanUserOpened(true),
          },
        ]
      : []),
    ...(isCanManagePinsAndNotReplyComment
      ? [
          commentData?.isPinned
            ? {
                id: 'comment-action-unpin',
                label: (
                  <>
                    <Pin02Icon className="size-5" />
                    {t('comments.actions.unpin')}
                  </>
                ),
                onSelect: handleUnpinComment,
              }
            : {
                id: 'comment-action-pin',
                label: (
                  <>
                    <Pin02Icon className="size-5" />
                    {t('comments.actions.pin')}
                  </>
                ),
                onSelect: handlePinComment,
              },
        ]
      : []),
    ...(canReportComment
      ? [
          {
            id: 'comment-action-report',
            label: (
              <>
                <ReportMessageIcon className="size-5" />
                {t('comments.actions.report')}
              </>
            ),
            onSelect: () => setIsReportOpened(true),
          },
        ]
      : []),
  ]

  if (
    !(
      canDeleteComment ||
      canUpdateComment ||
      canDeleteCommentAndBanAuthor ||
      isCanManagePinsAndNotReplyComment ||
      canReportComment
    )
  ) {
    return null
  }

  return (
    <>
      <div className="contents" onClick={e => e.stopPropagation()}>
        <Menu
          trigger={
            <button type="button">
              <div className="relative">
                <DotsVerticalIcon className="size-5" />
              </div>
            </button>
          }
          triggerProps={{ className }}
          items={menuItems}
          className="w-max min-w-[144px]"
        />
      </div>

      <ReportContentModal
        opened={isReportOpened}
        onClose={() => setIsReportOpened(false)}
        targetType="comment"
        isSubmitting={reportCommentMutation.isPending}
        onSubmit={async body => {
          await reportCommentMutation.mutateAsync({
            body,
            communityPath,
            postId,
            commentId: commentData.id,
          })
        }}
      />

      {isDeleteAndBanUserOpened && (
        <Confirmation
          onConfirm={handleDeleteAndBanUser}
          onCloseModal={() => setIsDeleteAndBanUserOpened(false)}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.delete_comment_and_ban_user_confirmation.label')}
              memberDisplayName={commentData.userName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => setRemoveRecentActivitiesChecked(prev => !prev)}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isDeleteCommentOpened && (
        <Confirmation
          onConfirm={() =>
            deleteCommentMutation.mutateAsync(undefined, {
              onSuccess: () => {
                toastManager.add({
                  type: 'success',
                  title: t('comments.actions.delete_comment.success'),
                })
              },
            })
          }
          onCloseModal={() => setIsDeleteCommentOpened(false)}
          confirmationContent={t('comments.components.comments.delete_confirmation_label')}
        />
      )}
      {switchPinnedCommentModalOpened && (
        <Confirmation
          onConfirm={handleSwitchPinnedComment}
          onCloseModal={() => setSwitchPinnedCommentModalOpened(false)}
          confirmationContent={t('comments.actions.switch_pin_comment.confirmation.content')}
        />
      )}
    </>
  )
}
